// Order Summary - Flows with page content

import { formatMoney } from '../lib/money';
import { useState } from 'react';
import { useCheckoutStore, useTotalWithShipping } from '../store/checkout';
import { getMarketConfig } from '../config/market';
import styles from './OrderSummary.module.css';

// Payment method logos in correct order
const PAYMENT_METHOD_LOGOS: Record<string, { url: string; alt: string; methods: string[] }> = {
  visa: {
    url: 'https://res.cloudinary.com/martinsurf/image/upload/v1764765667/surfboardmerchant/hpxkzcghdvo2duf4a9cm.png',
    alt: 'Visa',
    methods: ['CARD', 'VISA', 'CPOC'],
  },
  mastercard: {
    url: 'https://res.cloudinary.com/martinsurf/image/upload/v1764765654/surfboardmerchant/e4knt66x4mjaspstalbn.png',
    alt: 'Mastercard',
    methods: ['CARD', 'MASTERCARD', 'CPOC'],
  },
  amex: {
    url: 'https://res.cloudinary.com/martinsurf/image/upload/v1764765642/surfboardmerchant/ou2r1voubvuwizffjeec.png',
    alt: 'American Express',
    methods: ['AMEX'],  // AMEX requires explicit activation, not included with CPOC
  },
  vipps: {
    url: 'https://res.cloudinary.com/martinsurf/image/upload/v1764765541/surfboardmerchant/b1x670kyqav9izugemri.png',
    alt: 'Vipps',
    methods: ['VIPPS', 'SVIPPS'],
  },
  klarna: {
    url: 'https://res.cloudinary.com/martinsurf/image/upload/v1764765566/surfboardmerchant/yksx3mcpjg7q0zaina7d.png',
    alt: 'Klarna',
    methods: ['KLARNA'],
  },
  applePay: {
    url: 'https://res.cloudinary.com/martinsurf/image/upload/v1764765618/surfboardmerchant/mh5toqaql7cgorpv4joc.png',
    alt: 'Apple Pay',
    methods: ['CARD', 'APPLE_PAY', 'APPLEPAY', 'CPOC'],  // ApplePay available with card terminals
  },
  googlePay: {
    url: 'https://res.cloudinary.com/martinsurf/image/upload/v1764765606/surfboardmerchant/jh6lnsjw78phfwcufxdl.png',
    alt: 'Google Pay',
    methods: ['CARD', 'GOOGLE_PAY', 'GOOGLEPAY', 'CPOC'],  // GooglePay available with card terminals
  },
  swish: {
    url: 'https://www.surfboardpayments.com/img/methods/swish.svg',
    alt: 'Swish',
    methods: ['SWISH', 'SSWISH', 'NSWISH'],
  },
};

// Order of display (correct order as specified)
const LOGO_ORDER = ['visa', 'mastercard', 'amex', 'vipps', 'klarna', 'applePay', 'googlePay', 'swish'];

export function OrderSummary() {
  const cart = useCheckoutStore((s) => s.cart);
  const merchant = useCheckoutStore((s) => s.merchant);
  const selectedShipping = useCheckoutStore((s) => s.selectedShipping);
  const locale = useCheckoutStore((s) => s.locale);
  const market = getMarketConfig(locale);
  const L = market.labels;
  const totalWithShipping = useTotalWithShipping();
  const [showDetails, setShowDetails] = useState(false);

  const numberLocale = market.sdkLocale === 'no' ? 'nb-NO' : market.sdkLocale === 'sv' ? 'sv-SE' : market.sdkLocale === 'da' ? 'da-DK' : market.sdkLocale === 'fi' ? 'fi-FI' : 'en-US';

  const formatPrice = (amount: number, currency: string = market.defaultCurrency) =>
    formatMoney(amount, currency, numberLocale);

  const total = totalWithShipping || cart?.totalAmount || { amount: 0, currency: 'NOK' };
  
  // Get activated payment methods from merchant config
  const activatedMethods = merchant?.activatedPaymentMethods || ['CARD', 'VIPPS'];
  const currency = cart?.totalAmount?.currency || 'NOK';

  // Currency-based filtering for payment logos:
  // - Vipps: Only NOK
  // - Swish: Only SEK
  // - Card/Klarna/ApplePay/GooglePay: Always available
  const CURRENCY_RESTRICTED_METHODS: Record<string, string> = {
    vipps: 'NOK',
    swish: 'SEK',
  };

  // Check if a payment logo should be shown based on activated methods AND currency
  const isMethodActivated = (logoKey: string): boolean => {
    const logoConfig = PAYMENT_METHOD_LOGOS[logoKey];
    if (!logoConfig) return false;
    
    // Check currency restriction
    const requiredCurrency = CURRENCY_RESTRICTED_METHODS[logoKey];
    if (requiredCurrency && currency !== requiredCurrency) {
      return false;
    }
    
    return logoConfig.methods.some((method) => activatedMethods.includes(method));
  };

  // Get list of logos to display
  const logosToDisplay = LOGO_ORDER.filter(isMethodActivated);

  return (
    <div className={styles.summary}>
      <div className={styles.container}>
        <div className={styles.row}>
          <div>
            <div className={styles.label}>{L.orderAmountLabel}</div>
            <div className={styles.sublabel}>{L.inclVatShipping}</div>
          </div>
          <div>
            <div className={styles.amount}>{formatPrice(total.amount, total.currency)}</div>
            <button 
              className={styles.detailsLink}
              onClick={() => setShowDetails(!showDetails)}
              aria-expanded={showDetails}
              aria-controls="order-details"
            >
              {showDetails ? L.hideDetails : L.showDetails}
            </button>
          </div>
        </div>

        <div id="order-details" className={`${styles.details} ${!showDetails ? styles.detailsHidden : ''}`} role="region" aria-label={L.orderDetailsAria}>
          {cart?.items.map((item) => (
            <div key={item.id} className={styles.lineItem}>
              <div className={styles.itemName}>
                <span>{item.name}</span>
                <span className={styles.itemQty}>×{item.quantity}</span>
              </div>
              <span>{formatPrice(item.unitPrice.amount * item.quantity, item.unitPrice.currency)}</span>
            </div>
          ))}
          
          {selectedShipping && (
            <>
              <div className={styles.divider} />
              <div className={styles.lineItem}>
                <span>{L.shippingCostLabel} ({selectedShipping.displayName})</span>
                <span>
                  {selectedShipping.price.amount === 0 
                    ? L.freeLabel 
                    : formatPrice(selectedShipping.price.amount, selectedShipping.price.currency)
                  }
                </span>
              </div>
            </>
          )}
        </div>

        <div className={styles.paymentIcons}>
          {logosToDisplay.map((logoKey) => {
            const logo = PAYMENT_METHOD_LOGOS[logoKey];
            return (
              <img
                key={logoKey}
                src={logo.url}
                alt={logo.alt}
                className={styles.paymentIcon}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
